import React from 'react';
import { useFeature } from 'flagged';
import sortBy from 'lodash/sortBy';
import useSWR from 'swr';
import type ConstituentSearchColumn from '@/constants/ConstituentSearchColumn';
import FeatureFlags from '@/constants/system/FeatureFlags';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import fetcherWithDataKey from '@/lib/swr/fetcherWithDataKey';
import type AdvancedConstituentSearchFilter from '@/types/AdvancedConstituentSearchFilter';
import { createApiRequest } from '@/utils/services';
import mapOldSavedFilters from '../utils/mapOldSavedFilters';

type SavedFilterAPIType = {
  advancedFilters?: AdvancedConstituentSearchFilter[];
  filterName: string;
  ownerOrgId: string;
  savedFilterId: string;
  userId: string;
  userQuery: string;
};

export type AdvancedSearchUserQueryType = {
  advancedFilters?: AdvancedConstituentSearchFilter[];
  filters?: AdvancedConstituentSearchFilter[];
  columns?: ConstituentSearchColumn[];
  customFieldColumns?: string[];
};

export type SavedFilterType = Omit<SavedFilterAPIType, 'userQuery'> & {
  userQuery: AdvancedSearchUserQueryType;
};

type SavedFilterRequestType = {
  method: string;
  data: Pick<SavedFilterAPIType, 'filterName' | 'userQuery'> & {
    filterType: string;
  };
  path: string;
  throwErrors: boolean;
};

const getKey = (ownerOrgId: string, searchQuery?: string) => {
  let key = '';
  if (ownerOrgId) {
    key += `orgs/${ownerOrgId}/saved-filters/search?page_size=1000`;
    if (searchQuery) {
      key += `&filter_name=${searchQuery}`;
    }
  }
  return key;
};

const fetcher = fetcherWithDataKey<SavedFilterAPIType[]>('savedFilters');

const useSavedFilters = (searchQuery = '') => {
  const hasColumnSelection = useFeature(
    FeatureFlags.CONSTITUENT_COLUMN_SELECTION,
  );

  const ownerOrgId = useOwnerOrgId();
  const { data: customFields } = useCustomFields();

  const { data, mutate } = useSWR(getKey(ownerOrgId, searchQuery), fetcher);

  const savedFilters = React.useMemo<SavedFilterType[] | undefined>(() => {
    if (!(customFields && data)) return undefined;
    return data.map(({ userQuery, ...rest }) => {
      const queryObj: Record<string, unknown> = JSON.parse(
        decodeURIComponent(userQuery),
      );
      const {
        advancedFilters,
        filters,
        columns,
        customFieldColumns,
        ...oldFilters
      } = queryObj;
      const newUserQuery: AdvancedSearchUserQueryType = { filters: [] };

      if (advancedFilters || filters) {
        if (Array.isArray(advancedFilters) && advancedFilters.length) {
          newUserQuery.advancedFilters = advancedFilters;
        }
        if (Array.isArray(filters) && filters.length) {
          newUserQuery.filters = filters;
        }
        if (hasColumnSelection) {
          // As of now, we require a filter to be applied to save columns in a saved filter
          if (Array.isArray(columns) && columns.length) {
            newUserQuery.columns = columns;
          }
          if (Array.isArray(customFieldColumns) && customFieldColumns.length) {
            newUserQuery.customFieldColumns = customFieldColumns;
          }
        }
      } else {
        newUserQuery.filters = mapOldSavedFilters(oldFilters, customFields);
      }

      const result = {
        ...rest,
        userQuery: newUserQuery,
      };

      return result;
    });
  }, [customFields, data]);

  const addFilter = React.useCallback(
    async (filterName: string, filterObj: AdvancedSearchUserQueryType) => {
      // Check for any existing filters with the same filter name before saving.
      const res = await fetcher(
        // TODO(jon): We're omitting filterName here because initial tests were
        // giving false negatives from the search API when adding filterName as
        // a parameter. This is a re-indexing issue. Will re-add later when BE
        // has had a chance to migrate the service to Aurora.
        getKey(
          ownerOrgId,
          // filterName
        ),
      );
      if (
        res?.some((existingFilter) => existingFilter.filterName === filterName)
      ) {
        throw new Error(translate('CONSTITUENT_FILTER_DUPLICATE_ERROR'));
      }

      const userQuery = JSON.stringify(filterObj);
      const response = await createApiRequest<
        SavedFilterRequestType,
        APIResponse<{ savedFilter: SavedFilterAPIType }>
      >({
        method: 'post',
        path: `orgs/${ownerOrgId}/saved-filters`,
        data: { filterName, userQuery, filterType: 'constituents list' },
        throwErrors: true,
      });
      mutate(
        (currentItems) =>
          sortBy(
            [...(currentItems || []), response.data.savedFilter],
            'filterName',
          ),
        { revalidate: false },
      );
    },
    [data, mutate, ownerOrgId],
  );

  const removeFilter = React.useCallback(
    async (filterId: string) => {
      await createApiRequest({
        method: 'delete',
        path: `orgs/${ownerOrgId}/saved-filters/${filterId}`,
        throwErrors: true,
      });
      mutate(
        (currentItems) =>
          (currentItems || []).filter(
            ({ savedFilterId }) => savedFilterId !== filterId,
          ),
        { revalidate: false },
      );
    },
    [mutate, ownerOrgId],
  );

  return {
    addFilter,
    removeFilter,
    savedFilters,
  };
};

export default useSavedFilters;
